import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight, 
  Check, 
  ChevronDown,
  Globe,
  Database,
  Cpu,
  Zap,
  BarChart3,
  Users,
  Settings,
  Layers,
  GitBranch,
  MessageSquare,
  Calendar,
  FileText,
  Bell,
  TrendingUp,
  Search,
  PenTool,
  Rocket,
  Target,
  Play
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageTracking } from '../../hooks/useAnalytics';
import TestimonialsSection from '../../components/TestimonialsSection';
import FooterSection, { PHONE_NUMBER, PHONE_LINK, WHATSAPP_LINK, EMAIL } from '../../components/FooterSection';

// ========================================
// DIGITAL SYSTEM ORBIT COMPONENT
// ========================================
const DigitalSystemOrbit = ({ activeNode, onNodeClick }) => {
  const orbitRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const nodes = [
    { id: 'website', label: 'Website', icon: Globe, angle: 0, color: '#0ea5e9' },
    { id: 'crm', label: 'CRM', icon: Database, angle: 72, color: '#10b981' },
    { id: 'webapp', label: 'Web App', icon: Layers, angle: 144, color: '#8b5cf6' },
    { id: 'automation', label: 'Automazioni', icon: Zap, angle: 216, color: '#f59e0b' },
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, angle: 288, color: '#ec4899' }
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!orbitRef.current) return;
      const rect = orbitRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      setMousePosition({ x: x * 20, y: y * 20 });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div 
      ref={orbitRef}
      className="relative w-full h-[500px] md:h-[600px]"
      style={{ perspective: '1000px' }}
    >
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-radial from-sky-500/10 via-transparent to-transparent" />
      
      {/* Connection lines SVG */}
      <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#0ea5e9" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        {nodes.map((node, i) => {
          const nextNode = nodes[(i + 1) % nodes.length];
          const radius = 180;
          const x1 = 50 + Math.cos((node.angle * Math.PI) / 180) * (radius / 3);
          const y1 = 50 + Math.sin((node.angle * Math.PI) / 180) * (radius / 3);
          const x2 = 50 + Math.cos((nextNode.angle * Math.PI) / 180) * (radius / 3);
          const y2 = 50 + Math.sin((nextNode.angle * Math.PI) / 180) * (radius / 3);
          return (
            <motion.line
              key={`line-${i}`}
              x1={`${x1}%`}
              y1={`${y1}%`}
              x2={`${x2}%`}
              y2={`${y2}%`}
              stroke="url(#lineGradient)"
              strokeWidth="1"
              strokeDasharray="5,5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: i * 0.2 }}
            />
          );
        })}
      </svg>

      {/* Central Hub */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
        style={{
          transform: `translate(-50%, -50%) rotateX(${mousePosition.y}deg) rotateY(${mousePosition.x}deg)`
        }}
        transition={{ type: 'spring', stiffness: 100 }}
      >
        <div className="relative">
          {/* Outer glow ring */}
          <div className="absolute -inset-4 bg-sky-500/20 rounded-full blur-xl animate-pulse" />
          
          {/* Main hub */}
          <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-[#111] to-[#0a0a0a] border border-sky-500/50 flex items-center justify-center shadow-2xl shadow-sky-500/20">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>M&M</div>
              <div className="text-[10px] md:text-xs text-sky-400 uppercase tracking-widest mt-1">Digital System</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Orbiting Nodes */}
      {nodes.map((node, index) => {
        const Icon = node.icon;
        const radius = typeof window !== 'undefined' && window.innerWidth < 768 ? 140 : 200;
        const x = Math.cos((node.angle * Math.PI) / 180) * radius;
        const y = Math.sin((node.angle * Math.PI) / 180) * radius;
        const isActive = activeNode === node.id;
        
        return (
          <motion.div
            key={node.id}
            data-testid={`orbit-node-${node.id}`}
            className="absolute left-1/2 top-1/2 z-20 cursor-pointer"
            style={{
              x: x - 40,
              y: y - 40,
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: 1, 
              scale: isActive ? 1.15 : 1,
              x: x - 40 + mousePosition.x * 0.5,
              y: y - 40 + mousePosition.y * 0.5
            }}
            transition={{ 
              duration: 0.8, 
              delay: index * 0.15,
              type: 'spring',
              stiffness: 100
            }}
            onClick={() => onNodeClick(node.id)}
            whileHover={{ scale: 1.1 }}
          >
            <div 
              className={`
                relative w-20 h-20 md:w-24 md:h-24 rounded-2xl 
                bg-[#111]/90 backdrop-blur-xl
                border transition-all duration-300
                flex flex-col items-center justify-center gap-1
                ${isActive ? 'border-sky-500 shadow-lg shadow-sky-500/30' : 'border-white/10 hover:border-white/30'}
              `}
            >
              {/* Glow effect on active */}
              {isActive && (
                <div className="absolute -inset-1 bg-sky-500/20 rounded-2xl blur-lg" />
              )}
              
              <Icon 
                size={24} 
                className={`relative z-10 transition-colors ${isActive ? 'text-sky-400' : 'text-white/70'}`}
              />
              <span className={`relative z-10 text-[10px] md:text-xs font-medium transition-colors ${isActive ? 'text-white' : 'text-white/60'}`}>
                {node.label}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// ========================================
// FLOATING UI CARDS
// ========================================
const FloatingUICard = ({ children, className = '', delay = 0, x = 0, y = 0 }) => {
  return (
    <motion.div
      className={`
        absolute bg-[#111]/80 backdrop-blur-xl rounded-xl border border-white/10
        shadow-2xl shadow-black/50 ${className}
      `}
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, delay, type: 'spring' }}
      style={{ x, y }}
      whileHover={{ y: -5, borderColor: 'rgba(14, 165, 233, 0.3)' }}
    >
      {children}
    </motion.div>
  );
};

// Mini Dashboard Card
const MiniDashboardCard = () => (
  <div className="p-3 w-48">
    <div className="flex items-center gap-2 mb-2">
      <BarChart3 size={14} className="text-sky-400" />
      <span className="text-[10px] text-white/60 uppercase tracking-wider">Analytics</span>
    </div>
    <div className="flex gap-1">
      {[40, 65, 45, 80, 55, 70, 90].map((h, i) => (
        <motion.div
          key={i}
          className="flex-1 bg-sky-500/30 rounded-sm"
          initial={{ height: 0 }}
          animate={{ height: h * 0.4 }}
          transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
        />
      ))}
    </div>
    <div className="mt-2 flex justify-between text-[10px]">
      <span className="text-white/40">Visite</span>
      <span className="text-sky-400 font-medium">+24%</span>
    </div>
  </div>
);

// Mini Lead Notification
const MiniLeadCard = () => (
  <div className="p-3 w-44">
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
        <Bell size={12} className="text-emerald-400" />
      </div>
      <div>
        <div className="text-[10px] text-emerald-400 font-medium">Nuovo Lead</div>
        <div className="text-[9px] text-white/50">Mario R. - Preventivo</div>
      </div>
    </div>
  </div>
);

// Mini CRM Card
const MiniCRMCard = () => (
  <div className="p-3 w-40">
    <div className="flex items-center gap-2 mb-2">
      <Users size={12} className="text-purple-400" />
      <span className="text-[10px] text-white/60">CRM</span>
    </div>
    <div className="space-y-1">
      {['In contatto', 'Preventivo', 'Chiuso'].map((status, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-blue-400' : 'bg-emerald-400'}`} />
          <span className="text-[9px] text-white/50">{status}</span>
        </div>
      ))}
    </div>
  </div>
);

// ========================================
// SYSTEM FLOW COMPONENT
// ========================================
const SystemFlowAnimator = () => {
  const flowRef = useRef(null);
  const isInView = useInView(flowRef, { once: true, margin: '-100px' });
  
  const steps = [
    { icon: MessageSquare, label: 'Richiesta', desc: 'Il cliente compila il form' },
    { icon: Database, label: 'CRM', desc: 'Lead salvato automaticamente' },
    { icon: Zap, label: 'Automazione', desc: 'Email + notifica instant' },
    { icon: Calendar, label: 'Follow-up', desc: 'Reminder programmato' }
  ];

  return (
    <div ref={flowRef} className="relative py-12">
      {/* Flow line */}
      <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-500/30 to-transparent" />
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 relative">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={index}
              className="relative flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              {/* Connector dot */}
              <motion.div
                className="absolute -top-6 w-3 h-3 rounded-full bg-sky-500"
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ duration: 0.3, delay: index * 0.2 + 0.3 }}
              />
              
              {/* Card */}
              <div className="bg-[#111]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-sky-500/30 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center mb-4 group-hover:bg-sky-500/20 transition-colors">
                  <Icon size={24} className="text-sky-400" />
                </div>
                <h4 className="text-white font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.label}</h4>
                <p className="text-white/50 text-sm">{step.desc}</p>
              </div>
              
              {/* Arrow (not on last) */}
              {index < steps.length - 1 && (
                <motion.div
                  className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-sky-500/50"
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.3, delay: index * 0.2 + 0.5 }}
                >
                  <ArrowRight size={20} />
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// ========================================
// INTERACTIVE PROCESS COMPONENT
// ========================================
const InteractiveProcess = () => {
  const [activeStep, setActiveStep] = useState(0);
  const processRef = useRef(null);
  const isInView = useInView(processRef, { once: true, margin: '-50px' });
  
  const steps = [
    {
      num: '01',
      title: 'Ascolto',
      desc: 'Comprendiamo la tua esigenza aziendale, i processi attuali e gli obiettivi che vuoi raggiungere.',
      visual: 'problem',
      icon: Search
    },
    {
      num: '02', 
      title: 'Analisi',
      desc: 'Valutiamo le opzioni disponibili e progettiamo la soluzione digitale più adatta al tuo caso.',
      visual: 'architecture',
      icon: PenTool
    },
    {
      num: '03',
      title: 'Sviluppo',
      desc: 'Realizziamo il sistema con tecnologie moderne, testandolo e perfezionandolo con te.',
      visual: 'development',
      icon: Settings
    },
    {
      num: '04',
      title: 'Evoluzione',
      desc: 'Il sistema cresce con la tua azienda. Aggiungiamo funzionalità quando servono.',
      visual: 'growth',
      icon: Rocket
    }
  ];

  return (
    <div ref={processRef} className="grid lg:grid-cols-2 gap-12 items-center">
      {/* Left: Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = activeStep === index;
          
          return (
            <motion.div
              key={index}
              data-testid={`process-step-${index}`}
              className={`
                relative p-6 rounded-2xl cursor-pointer transition-all duration-300
                ${isActive 
                  ? 'bg-[#111] border border-sky-500/50 shadow-lg shadow-sky-500/10' 
                  : 'bg-transparent border border-transparent hover:bg-white/5'}
              `}
              onClick={() => setActiveStep(index)}
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="flex items-start gap-4">
                <div className={`
                  w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                  ${isActive ? 'bg-sky-500/20' : 'bg-white/5'}
                `}>
                  <Icon size={24} className={isActive ? 'text-sky-400' : 'text-white/40'} />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`text-sm font-mono ${isActive ? 'text-sky-400' : 'text-white/30'}`}>
                      {step.num}
                    </span>
                    <h4 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {step.title}
                    </h4>
                  </div>
                  <AnimatePresence mode="wait">
                    {isActive && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-white/60 text-sm leading-relaxed"
                      >
                        {step.desc}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Right: Visual */}
      <div className="relative h-[400px] hidden lg:block">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            exit={{ opacity: 0, scale: 0.9, rotateY: 15 }}
            transition={{ duration: 0.5 }}
          >
            <ProcessVisual step={activeStep} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Process Visual Component
const ProcessVisual = ({ step }) => {
  const visuals = [
    // Step 0: Problem - Disconnected tools
    <div key="0" className="relative w-full h-full flex items-center justify-center">
      <div className="grid grid-cols-2 gap-4">
        {['Excel', 'Email', 'Agenda', 'Note'].map((tool, i) => (
          <motion.div
            key={tool}
            className="w-24 h-24 bg-white/5 rounded-xl border border-white/10 flex items-center justify-center"
            initial={{ opacity: 0, rotate: -10 }}
            animate={{ opacity: 0.5, rotate: (i % 2 === 0 ? -5 : 5) }}
            transition={{ delay: i * 0.1 }}
          >
            <span className="text-white/30 text-xs">{tool}</span>
          </motion.div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="text-red-400/50 text-6xl">?</span>
      </div>
    </div>,
    
    // Step 1: Architecture - Blueprint
    <div key="1" className="relative w-full h-full">
      <svg className="w-full h-full" viewBox="0 0 300 300">
        <motion.rect
          x="100" y="50" width="100" height="60" rx="8"
          fill="none" stroke="#0ea5e9" strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1 }}
        />
        <motion.rect
          x="50" y="150" width="80" height="50" rx="8"
          fill="none" stroke="#0ea5e9" strokeWidth="2" strokeOpacity="0.5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        />
        <motion.rect
          x="170" y="150" width="80" height="50" rx="8"
          fill="none" stroke="#0ea5e9" strokeWidth="2" strokeOpacity="0.5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
        />
        <motion.line
          x1="150" y1="110" x2="90" y2="150"
          stroke="#0ea5e9" strokeWidth="1" strokeOpacity="0.3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        />
        <motion.line
          x1="150" y1="110" x2="210" y2="150"
          stroke="#0ea5e9" strokeWidth="1" strokeOpacity="0.3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        />
        <text x="150" y="85" textAnchor="middle" fill="white" fontSize="12">Sistema</text>
        <text x="90" y="180" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10">CRM</text>
        <text x="210" y="180" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10">Web</text>
      </svg>
    </div>,
    
    // Step 2: Development - Code/UI
    <div key="2" className="relative w-full h-full flex items-center justify-center">
      <div className="w-64 h-48 bg-[#0a0a0a] rounded-xl border border-white/10 overflow-hidden">
        <div className="h-6 bg-white/5 flex items-center gap-1.5 px-3">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/50" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400/50" />
        </div>
        <div className="p-3 font-mono text-[10px] space-y-1">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <span className="text-purple-400">const</span> <span className="text-sky-400">sistema</span> <span className="text-white">=</span> <span className="text-yellow-400">{'{'}</span>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="pl-3">
            <span className="text-white/50">crm:</span> <span className="text-emerald-400">true</span>,
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="pl-3">
            <span className="text-white/50">automation:</span> <span className="text-emerald-400">true</span>,
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="pl-3">
            <span className="text-white/50">analytics:</span> <span className="text-emerald-400">true</span>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
            <span className="text-yellow-400">{'}'}</span>;
          </motion.div>
        </div>
      </div>
    </div>,
    
    // Step 3: Growth - Expanding system
    <div key="3" className="relative w-full h-full flex items-center justify-center">
      <div className="relative">
        {[0, 1, 2].map((ring) => (
          <motion.div
            key={ring}
            className="absolute rounded-full border border-sky-500"
            style={{
              width: 100 + ring * 80,
              height: 100 + ring * 80,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.3 - ring * 0.1 }}
            transition={{ duration: 1, delay: ring * 0.3 }}
          />
        ))}
        <motion.div
          className="relative w-20 h-20 bg-sky-500/20 rounded-full flex items-center justify-center border border-sky-500"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
        >
          <TrendingUp size={32} className="text-sky-400" />
        </motion.div>
      </div>
    </div>
  ];
  
  return visuals[step] || visuals[0];
};

// ========================================
// MAIN COMPONENT
// ========================================
const SeoLandingPagePremium = ({ 
  city = "Sesto San Giovanni",
  cityId = "sesto-san-giovanni",
  region = "Milano",
  serviceId = "web-designer",
  slug = "web-designer-sesto-san-giovanni",
  metaTitle = "Web Designer Sesto San Giovanni | Sistemi Digitali per Aziende | M&M",
  metaDescription = "Web designer a Sesto San Giovanni: progettiamo siti web, web app, gestionali e automazioni per aziende e professionisti. Consulenza gratuita.",
  heroTitle = "Web Designer a",
  heroHighlight = "sistemi digitali su misura",
  heroSubtitle = "Cerchi un web designer a Sesto San Giovanni? Siamo sviluppatori di sistemi digitali completi: non solo siti, ma web app, CRM personalizzati, gestionali e automazioni che trasformano il modo in cui lavori.",
  customContent = null
}) => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeOrbitNode, setActiveOrbitNode] = useState('website');
  const pagePath = `/${slug}`;
  const { trackClick } = usePageTracking(pagePath);
  const canonicalUrl = `https://www.mm-webdesigners.it${pagePath}`;
  
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  // Use custom content if available
  const problems = customContent?.problems || [
    "Hai bisogno di digitalizzare processi ma non sai da dove iniziare?",
    "Cerchi un partner che comprenda le esigenze della tua azienda?",
    "Vorresti un sistema su misura, non un prodotto standardizzato?",
    "I tuoi strumenti digitali attuali non comunicano tra loro?"
  ];

  const solutions = customContent?.solutions || [
    { title: "Analisi delle esigenze", desc: "Partiamo dal problema aziendale, non dalla tecnologia" },
    { title: "Progettazione su misura", desc: "Siti, web app, gestionali, automazioni: ciò che serve davvero" },
    { title: "Sviluppo professionale", desc: "Tecnologie moderne, codice pulito, performance elevate" },
    { title: "Supporto continuativo", desc: "Il progetto evolve con la tua azienda" }
  ];

  const faqs = customContent?.faqs || [
    {
      q: "Quali tipi di progetti realizzate?",
      a: "Progettiamo siti web aziendali, landing page, e-commerce, web app, gestionali personalizzati e automazioni. Ogni progetto parte dall'analisi delle esigenze specifiche per proporre la soluzione più adatta."
    },
    {
      q: "Cosa distingue un sistema digitale da un semplice sito web?",
      a: "Un sito web è una vetrina. Un sistema digitale può includere il sito, ma anche CRM per gestire i contatti, automazioni per follow-up, gestionali per preventivi e ordini. Ogni componente è progettato in base alle esigenze del progetto."
    },
    {
      q: "Come funziona la consulenza iniziale?",
      a: "La prima consulenza è gratuita e senza impegno. Analizziamo insieme la situazione attuale, le esigenze e gli obiettivi. Successivamente proponiamo una soluzione con preventivo dettagliato."
    },
    {
      q: "Posso partire con un sito semplice e aggiungere funzionalità dopo?",
      a: "Certamente, è l'approccio che consigliamo. Progettiamo sempre con la scalabilità in mente: oggi il sito, domani l'integrazione CRM, poi le automazioni. Ogni pezzo si incastra in un ecosistema coerente."
    }
  ];

  const ctaText = customContent?.ctaText || "Progettiamo il tuo sistema";
  const ctaSubtext = customContent?.ctaSubtext || "Raccontaci come lavori oggi. Ti mostreremo come potrebbe funzionare domani.";

  // SEO Meta tags effect
  useEffect(() => {
    document.title = metaTitle;
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', metaDescription);
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // Structured data
    const getServiceType = () => {
      switch(serviceId) {
        case 'web-designer': return 'Web Design and Digital Solutions';
        case 'realizzazione-siti-web': return 'Website Development';
        case 'creazione-siti-web-aziende': return 'Business Website Development';
        case 'sviluppo-siti-web-professionali': return 'Web Application Development';
        case 'siti-web-responsive-seo': return 'SEO and Responsive Web Design';
        default: return 'Web Design';
      }
    };

    let ldScript = document.querySelector('script[data-page-ld]');
    if (!ldScript) {
      ldScript = document.createElement('script');
      ldScript.type = 'application/ld+json';
      ldScript.setAttribute('data-page-ld', 'true');
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": metaTitle,
      "description": metaDescription,
      "url": canonicalUrl,
      "provider": {
        "@type": "ProfessionalService",
        "name": "M&M Web Designers",
        "url": "https://www.mm-webdesigners.it/"
      },
      "areaServed": { "@type": "City", "name": city },
      "serviceType": getServiceType()
    });

    // FAQ Schema
    if (faqs.length > 0) {
      let faqScript = document.querySelector('script[data-faq-ld]');
      if (!faqScript) {
        faqScript = document.createElement('script');
        faqScript.type = 'application/ld+json';
        faqScript.setAttribute('data-faq-ld', 'true');
        document.head.appendChild(faqScript);
      }
      faqScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      });
    }

    return () => {
      const pageLd = document.querySelector('script[data-page-ld]');
      if (pageLd) pageLd.remove();
      const faqLd = document.querySelector('script[data-faq-ld]');
      if (faqLd) faqLd.remove();
    };
  }, [metaTitle, metaDescription, canonicalUrl, city, serviceId, faqs]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Noise texture overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.015] z-50" 
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} 
      />

      {/* ========== HEADER STICKY ========== */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/images/logo.png" 
              alt="M&M Web Designers" 
              className="h-12 w-auto"
              data-testid="header-logo"
            />
          </Link>
          
          {/* Right side - Phone + CTA */}
          <div className="flex items-center gap-4">
            <a 
              href={PHONE_LINK}
              className="hidden md:flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              <Phone size={16} />
              {PHONE_NUMBER}
            </a>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-400 text-white text-sm font-medium rounded-full transition-colors"
            >
              Vai al sito principale
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </header>

      {/* ========== HERO SECTION ========== */}
      <motion.section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ opacity: heroOpacity, scale: heroScale }}
      >
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-radial from-sky-950/30 via-[#0a0a0a] to-[#0a0a0a]" />
        
        {/* Animated background lines */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <svg className="absolute w-full h-full" preserveAspectRatio="none">
            {[...Array(5)].map((_, i) => (
              <motion.line
                key={i}
                x1="0%"
                y1={`${20 + i * 15}%`}
                x2="100%"
                y2={`${30 + i * 12}%`}
                stroke="#0ea5e9"
                strokeWidth="0.5"
                strokeOpacity="0.3"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 3, delay: i * 0.5, repeat: Infinity, repeatType: 'loop' }}
              />
            ))}
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span>/</span>
                <span className="text-white/60">{city}</span>
              </div>

              {/* H1 */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {heroTitle}{' '}
                <span className="text-white">{city}</span>
                <br />
                <span className="bg-gradient-to-r from-sky-400 to-cyan-300 bg-clip-text text-transparent">
                  {heroHighlight}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-lg text-white/60 leading-relaxed mb-8 max-w-xl">
                {heroSubtitle}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="/#contatti"
                  data-testid="hero-cta-button"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-sky-500 hover:bg-sky-400 text-white font-semibold rounded-full transition-all duration-300 shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40"
                  onClick={() => trackClick('hero_cta')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Play size={18} />
                  Richiedi demo gratuita
                </motion.a>
                <motion.a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 text-white font-medium rounded-full border border-white/10 hover:border-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  <MessageSquare size={18} />
                  WhatsApp
                </motion.a>
              </div>
            </motion.div>

            {/* Right: Digital System Orbit */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <DigitalSystemOrbit 
                activeNode={activeOrbitNode} 
                onNodeClick={setActiveOrbitNode} 
              />
              
              {/* Floating UI Cards */}
              <FloatingUICard className="top-10 -left-4 md:left-0" delay={1}>
                <MiniDashboardCard />
              </FloatingUICard>
              
              <FloatingUICard className="bottom-20 -right-4 md:right-0" delay={1.2}>
                <MiniLeadCard />
              </FloatingUICard>
              
              <FloatingUICard className="top-1/2 -left-8 md:-left-4 hidden md:block" delay={1.4}>
                <MiniCRMCard />
              </FloatingUICard>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={24} className="text-white/30" />
        </motion.div>
      </motion.section>

      {/* ========== PROBLEM SECTION ========== */}
      <section className="py-24 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ti riconosci in una di queste situazioni?
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Ogni giorno incontriamo aziende e professionisti con queste sfide. La buona notizia? Hanno soluzioni concrete.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                className="p-6 bg-white/5 rounded-2xl border border-white/5 hover:border-sky-500/30 transition-all duration-300 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-sky-500/20 transition-colors">
                    <Target size={20} className="text-sky-400" />
                  </div>
                  <p className="text-white/80 leading-relaxed">{problem}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SYSTEM FLOW SECTION ========== */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Come funziona un sistema digitale
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Non costruiamo pezzi isolati. Progettiamo ecosistemi dove ogni componente comunica con gli altri.
            </p>
          </motion.div>

          <SystemFlowAnimator />
        </div>
      </section>

      {/* ========== SOLUTIONS SECTION ========== */}
      <section className="py-24 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              La nostra soluzione
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Un approccio consulenziale che parte dal problema per arrivare alla soluzione giusta.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                className="p-8 bg-gradient-to-br from-white/5 to-transparent rounded-2xl border border-white/10 hover:border-sky-500/30 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(14, 165, 233, 0.1)' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Check size={20} className="text-sky-400" />
                  <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {solution.title}
                  </h3>
                </div>
                <p className="text-white/60 leading-relaxed pl-8">{solution.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PROCESS SECTION ========== */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Il nostro processo
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Ogni progetto digitale parte da un&apos;esigenza, non da un template.
            </p>
          </motion.div>

          <InteractiveProcess />
        </div>
      </section>

      {/* ========== FAQ SECTION ========== */}
      <section className="py-24 px-6 bg-[#111]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Domande frequenti
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <button
                  data-testid={`faq-${index}`}
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-white pr-4">{faq.q}</span>
                  <motion.div
                    animate={{ rotate: openFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={20} className="text-white/50 flex-shrink-0" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-white/60 leading-relaxed">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS SECTION ========== */}
      <TestimonialsSection compact={true} />

      {/* ========== CTA SECTION ========== */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Glow background */}
            <div className="absolute inset-0 bg-gradient-radial from-sky-500/20 via-transparent to-transparent blur-3xl" />
            
            <div className="relative bg-gradient-to-br from-[#111] to-[#0a0a0a] rounded-3xl p-12 border border-white/10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Parliamo gratuitamente del tuo progetto
              </h2>
              <p className="text-white/60 mb-8 max-w-xl mx-auto">
                {ctaSubtext}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/#contatti"
                  data-testid="final-cta-button"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-sky-500 hover:bg-sky-400 text-white font-semibold rounded-full transition-all duration-300 shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40"
                  onClick={() => trackClick('final_cta')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Ottieni un preventivo senza impegno
                  <ArrowRight size={18} />
                </motion.a>
              </div>

              {/* Contact info */}
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-white/50">
                <a href={PHONE_LINK} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone size={16} />
                  {PHONE_NUMBER}
                </a>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#25D366] transition-colors">
                  <MessageSquare size={16} />
                  WhatsApp
                </a>
                <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail size={16} />
                  {EMAIL}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== INTERNAL LINKS ========== */}
      <section className="py-16 px-6 bg-[#111] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-lg font-semibold text-white/80 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Scopri i nostri servizi a {city}
          </h3>
          <div className="flex flex-wrap gap-3">
            {customContent?.internalLinks?.map((link, index) => (
              <Link
                key={index}
                to={link.url}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-sm text-white/60 hover:text-white transition-all duration-300 border border-white/5 hover:border-white/20"
              >
                {link.text}
              </Link>
            )) || (
              <>
                <Link to={`/realizzazione-siti-web-${cityId}`} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-sm text-white/60 hover:text-white transition-all duration-300 border border-white/5 hover:border-white/20">
                  Realizzazione siti web
                </Link>
                <Link to={`/sviluppo-siti-web-professionali-${cityId}`} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-sm text-white/60 hover:text-white transition-all duration-300 border border-white/5 hover:border-white/20">
                  Sviluppo professionale
                </Link>
                <Link to={`/siti-web-responsive-seo-${cityId}`} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-sm text-white/60 hover:text-white transition-all duration-300 border border-white/5 hover:border-white/20">
                  Siti responsive e SEO
                </Link>
                <Link to={`/creazione-siti-web-aziende-${cityId}`} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-sm text-white/60 hover:text-white transition-all duration-300 border border-white/5 hover:border-white/20">
                  Siti per aziende
                </Link>
              </>
            )}
          </div>
          
          {/* Location info */}
          <div className="mt-8 flex items-center gap-2 text-white/40">
            <MapPin size={16} />
            <span>Serviamo {city} e comuni limitrofi</span>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <FooterSection showNewsletter={false} />
    </div>
  );
};

export default SeoLandingPagePremium;
